import { useState } from 'react';
import {
  formatDuration,
  formatPace,
  paceSecondsPerKm,
  speedKph,
  type CardioMachineKind,
} from '@liftx/core';
import type { CardioSegmentRecord } from '../../data/types';
import styles from './Workout.module.css';

function parseIntOrNull(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

/** Like `parseIntOrNull`, but keeps decimals — an Incline previously saved as
 *  e.g. `6.5` must round-trip untouched. */
function parseFloatOrNull(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const parsed = Number.parseFloat(trimmed);
  return Number.isNaN(parsed) ? null : parsed;
}

/** Machine rows edit Time in minutes; storage stays seconds (`durationS`). */
function secondsToMinutesString(durationS: number | null): string {
  if (durationS === null) return '';
  return String(Math.round((durationS / 60) * 100) / 100);
}

function minutesToSeconds(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const parsed = Number.parseFloat(trimmed);
  return Number.isNaN(parsed) || parsed < 0 ? null : Math.round(parsed * 60);
}

/** Metres → "5.00 km", or "—" when there is nothing to show. */
function formatDistance(distanceM: number | null): string {
  if (distanceM === null) return '—';
  return `${(distanceM / 1000).toFixed(2)} km`;
}

/** 1–20 covers every machine Level dial LiftX has seen reason to model — a fixed dropdown, not free text, so there is nothing to mistype. */
const LEVEL_OPTIONS = Array.from({ length: 20 }, (_, i) => i + 1);

/** Normal treadmill incline settings, stored as-is in `inclinePct`. */
const INCLINE_OPTIONS = Array.from({ length: 16 }, (_, i) => i);

/** The standard options plus, when the persisted value is not one of them
 *  (e.g. a decimal `6.5` saved by the earlier free-number Incline field),
 *  that value itself — so the select can display it instead of silently
 *  rounding or blanking it. Choosing a standard option replaces it. */
function withCurrent(options: number[], current: string): string[] {
  const values = options.map(String);
  const parsed = Number.parseFloat(current);
  if (current !== '' && !Number.isNaN(parsed) && !values.includes(String(parsed))) values.push(String(parsed));
  return values.sort((a, b) => Number(a) - Number(b));
}

type SegmentValues = {
  durationS?: number | null;
  distanceM?: number | null;
  avgHeartRate?: number | null;
  resistanceLevel?: number | null;
  estimatedSpeedKph?: number | null;
  inclinePct?: number | null;
};

/**
 * One cardio segment — Cardio Phase C4, simplified for machine exercises by
 * the Level → estimated speed feature's UX pass.
 *
 * The sibling of `SetRow`, and deliberately the same interaction: the row IS
 * the editor, no modal, one tap to complete when the values are already
 * right (`PHASE-0-ARCHITECTURE.md` §14).
 *
 * `machineKind` (`null` for anything that isn't a treadmill/bike-named
 * exercise — see `CardioPanel`'s `cardioMachineKind`) branches the whole row:
 *
 * - `null` — unchanged from before this feature ever existed: duration (s),
 *   distance (m), heart rate (bpm), and the derived pace/speed line.
 * - Set — a genuinely different, much smaller row: a Level dropdown, a
 *   duration (Time) field, and the same Complete control. Distance/heart-rate
 *   inputs and the derived pace/speed/estimated-speed metrics do not apply
 *   here at all. This is a UI simplification only: `distanceM` and
 *   `avgHeartRate` are untouched on `CardioSegmentRecord`/`sessionRepository`
 *   — a machine segment simply has no editor for them here — and no
 *   automatic Level → speed calculation happens (`estimatedSpeedKph` is left
 *   exactly as it was, never written by this row); that mapping is a
 *   separate decision for later, not part of this pass. Picking a Level
 *   persists immediately (`onChange`, not blur — a `<select>` has no
 *   free-text state to lose); duration persists on blur, same as every other
 *   numeric field in this file.
 *
 * `machineKind === 'treadmill'` additionally gets an Incline selector (0–15, stored as `inclinePct`) on its
 * own secondary line, below the Level/Duration row (Treadmill Incline
 * feature) — a genuinely separate concept from Level, never replacing or
 * reusing it. Bike (`machineKind === 'bike'`) is deliberately left with only
 * Level/Duration: a stationary bike's incline has no established meaning the
 * way a treadmill's does, so nothing is invented for it here.
 */
export function CardioSegmentRow({
  segment,
  index,
  machineKind,
  onChange,
  onComplete,
  onUncomplete,
}: {
  segment: CardioSegmentRecord;
  index: number;
  /** `null` when this exercise's name matches no known machine-Level convention — see `CardioPanel`'s `cardioMachineKind`. */
  machineKind: CardioMachineKind | null;
  onChange: (values: SegmentValues) => void;
  onComplete: (values: SegmentValues) => void;
  onUncomplete: () => void;
}): React.JSX.Element {
  const [durationS, setDurationS] = useState(segment.durationS === null ? '' : String(segment.durationS));
  const [distanceM, setDistanceM] = useState(segment.distanceM === null ? '' : String(segment.distanceM));
  const [heartRate, setHeartRate] = useState(
    segment.avgHeartRate === null ? '' : String(segment.avgHeartRate),
  );
  const [minutes, setMinutes] = useState(secondsToMinutesString(segment.durationS));
  const [level, setLevel] = useState(segment.resistanceLevel == null ? '' : String(segment.resistanceLevel));
  const [incline, setIncline] = useState(segment.inclinePct == null ? '' : String(segment.inclinePct));

  function currentValues(): {
    durationS: number | null;
    distanceM: number | null;
    avgHeartRate: number | null;
  } {
    return {
      durationS: parseIntOrNull(durationS),
      distanceM: parseIntOrNull(distanceM),
      avgHeartRate: parseIntOrNull(heartRate),
    };
  }

  /** Picking a Level saves immediately — no blur step. The payload carries
   *  only `resistanceLevel`: no duration/distance/heart-rate keys (this row
   *  shows no editor for them) and, for now, no `estimatedSpeedKph` either —
   *  the UI-simplification pass this row went through deliberately stopped
   *  short of any automatic Level → speed calculation, leaving that a
   *  separate decision for later. Omitting the key (rather than sending
   *  `null`) means this never touches whatever `estimatedSpeedKph` already
   *  holds, either way. */
  function handleLevelChange(next: string): void {
    setLevel(next);
    onChange({ resistanceLevel: parseIntOrNull(next) });
  }

  /** Same immediate-save behaviour as Level; blank persists `null`, never `0`. */
  function handleInclineChange(next: string): void {
    setIncline(next);
    onChange({ inclinePct: parseFloatOrNull(next) });
  }

  // Derived from what is currently TYPED, not from the persisted row, so pace
  // updates as the athlete corrects a number rather than only after a save.
  const live = currentValues();
  const pace = paceSecondsPerKm(live.distanceM, live.durationS);
  const speed = speedKph(live.distanceM, live.durationS);

  // `Maybe` already models every degenerate case — missing distance, missing
  // duration, zero distance — so there is no arithmetic here that could
  // produce Infinity or NaN. An unknown value renders as an em dash.
  const paceLabel = pace.known ? formatPace(pace.value) : '—';
  const speedLabel = speed.known ? `${speed.value.toFixed(2)} km/h` : '—';

  const label = `Segment ${String(index + 1)}${segment.isCompleted ? ', completed' : ''}`;

  function handleToggle(): void {
    if (segment.isCompleted) {
      onUncomplete();
      return;
    }
    onComplete(
      machineKind !== null
        ? {
            resistanceLevel: parseIntOrNull(level),
            durationS: minutesToSeconds(minutes),
            ...(machineKind === 'treadmill' ? { inclinePct: parseFloatOrNull(incline) } : {}),
          }
        : currentValues(),
    );
  }

  if (machineKind !== null) {
    return (
      <li className={styles.cardioSegment} data-completed={segment.isCompleted} aria-label={label}>
        <div className={styles.setRow}>
          <span className={styles.setNumber} aria-hidden="true">
            {index + 1}
          </span>
          <div className={styles.setFields}>
            <label className={styles.setField}>
              <select
                className={styles.setInput}
                aria-label={`Segment ${String(index + 1)} machine level`}
                value={level}
                disabled={segment.isCompleted}
                onChange={(event) => handleLevelChange(event.target.value)}
              >
                <option value="">—</option>
                {withCurrent(LEVEL_OPTIONS, level).map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <span className={styles.setFieldUnit} aria-hidden="true">
                Level
              </span>
            </label>
            <span className={styles.setFieldDivider} aria-hidden="true" />
            <label className={styles.setField}>
              <input
                type="number"
                inputMode="decimal"
                step="any"
                min="0"
                className={styles.setInput}
                aria-label={`Segment ${String(index + 1)} duration in minutes`}
                placeholder="—"
                value={minutes}
                disabled={segment.isCompleted}
                onChange={(event) => setMinutes(event.target.value)}
                onBlur={() => onChange({ durationS: minutesToSeconds(minutes) })}
              />
              <span className={styles.setFieldUnit} aria-hidden="true">
                min
              </span>
            </label>
          </div>
          <button
            type="button"
            className={
              segment.isCompleted ? `${styles.completeButton} ${styles.completeButtonOn}` : styles.completeButton
            }
            role="checkbox"
            aria-checked={segment.isCompleted}
            aria-label={
              segment.isCompleted
                ? `Mark segment ${String(index + 1)} incomplete`
                : `Complete segment ${String(index + 1)}`
            }
            onClick={handleToggle}
          >
            {segment.isCompleted && (
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
                <path
                  d="M5.5 11.5l3.5 3.5 7.5-8"
                  stroke="currentColor"
                  strokeWidth="2.25"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
        </div>

        {machineKind === 'treadmill' && (
          <div className={styles.cardioSecondary}>
            <label className={styles.cardioHrField}>
              <select
                className={styles.setInput}
                aria-label={`Segment ${String(index + 1)} incline level`}
                value={incline}
                disabled={segment.isCompleted}
                onChange={(event) => handleInclineChange(event.target.value)}
              >
                <option value="">—</option>
                {withCurrent(INCLINE_OPTIONS, incline).map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <span className={styles.setFieldUnit} aria-hidden="true">
                Incline
              </span>
            </label>
          </div>
        )}
      </li>
    );
  }

  return (
    <li className={styles.cardioSegment} data-completed={segment.isCompleted} aria-label={label}>
      <div className={styles.setRow}>
        <span className={styles.setNumber} aria-hidden="true">
          {index + 1}
        </span>
        <div className={styles.setFields}>
          <label className={styles.setField}>
            <input
              type="number"
              inputMode="numeric"
              className={styles.setInput}
              aria-label={`Segment ${String(index + 1)} duration in seconds`}
              placeholder="—"
              value={durationS}
              disabled={segment.isCompleted}
              onChange={(event) => setDurationS(event.target.value)}
              onBlur={() => onChange(currentValues())}
            />
            <span className={styles.setFieldUnit} aria-hidden="true">
              s
            </span>
          </label>
          <span className={styles.setFieldDivider} aria-hidden="true" />
          <label className={styles.setField}>
            <input
              type="number"
              inputMode="numeric"
              className={styles.setInput}
              aria-label={`Segment ${String(index + 1)} distance in metres`}
              placeholder="—"
              value={distanceM}
              disabled={segment.isCompleted}
              onChange={(event) => setDistanceM(event.target.value)}
              onBlur={() => onChange(currentValues())}
            />
            <span className={styles.setFieldUnit} aria-hidden="true">
              m
            </span>
          </label>
        </div>
        <button
          type="button"
          className={
            segment.isCompleted ? `${styles.completeButton} ${styles.completeButtonOn}` : styles.completeButton
          }
          role="checkbox"
          aria-checked={segment.isCompleted}
          aria-label={
            segment.isCompleted
              ? `Mark segment ${String(index + 1)} incomplete`
              : `Complete segment ${String(index + 1)}`
          }
          onClick={handleToggle}
        >
          {segment.isCompleted && (
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
              <path
                d="M5.5 11.5l3.5 3.5 7.5-8"
                stroke="currentColor"
                strokeWidth="2.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </button>
      </div>

      {/* Heart rate sits on its own line rather than as a third field in the
          pill above: three numeric inputs plus a 56px touch target does not
          fit a 375px phone without shrinking something below its floor. */}
      <div className={styles.cardioSecondary}>
        <label className={styles.cardioHrField}>
          <input
            type="number"
            inputMode="numeric"
            className={styles.setInput}
            aria-label={`Segment ${String(index + 1)} average heart rate in beats per minute`}
            placeholder="—"
            value={heartRate}
            disabled={segment.isCompleted}
            onChange={(event) => setHeartRate(event.target.value)}
            onBlur={() => onChange(currentValues())}
          />
          <span className={styles.setFieldUnit} aria-hidden="true">
            bpm
          </span>
        </label>

        <p className={styles.cardioMetrics}>
          <span>{live.durationS === null ? '—' : formatDuration(live.durationS)}</span>
          <span aria-hidden="true">·</span>
          <span>{formatDistance(live.distanceM)}</span>
          <span aria-hidden="true">·</span>
          <span>{paceLabel}</span>
          <span aria-hidden="true">·</span>
          <span>{speedLabel}</span>
        </p>
      </div>
    </li>
  );
}
